import { type Storage } from '@/infra/storage'
import { createObjectStorage } from '@/infra/object-storage'

/**
 * Special value to indicate "All workspaces" was explicitly selected.
 */
export const ALL_WORKSPACES_SELECTION = '__ALL_WORKSPACES__'

type LastWorkspaceByUser = Record<string, string>

/**
 * Remembers the workspace each user last selected.
 */
export interface LastWorkspaceStorage {
  /**
   * Gets the ID of the workspace the user last selected, if any.
   *
   * @param userId
   */
  load(userId: string): string | null

  /**
   * Saves the given ID as the user's last selected workspace.
   *
   * @param workspaceId
   * @param userId
   */
  save(workspaceId: string, userId: string): void

  /**
   * Forgets the user's last selected workspace.
   *
   * @param userId
   */
  clear(userId: string): void
}

/**
 * Default implementation, namespaced so each page can remember its own
 * selection.
 */
export class DefaultLastWorkspaceStorage implements LastWorkspaceStorage {
  private readonly _key: string
  private readonly _storage

  constructor(storage: Storage, namespace?: string) {
    this._storage = createObjectStorage(storage)
    this._key = namespace
      ? `lastWorkspaceByUser.${namespace}`
      : 'lastWorkspaceByUser'
  }

  load = (userId: string) => {
    const item = this._storage.getItem(this._key) as LastWorkspaceByUser | null
    return item ? (item[userId] ?? null) : null
  }

  save = (workspaceId: string, userId: string) => {
    const current = this._storage.getItem(
      this._key,
    ) as LastWorkspaceByUser | null
    this._storage.setItem(this._key, { ...current, [userId]: workspaceId })
  }

  clear = (userId: string) => {
    const current = this._storage.getItem(
      this._key,
    ) as LastWorkspaceByUser | null
    if (current) {
      const updated = { ...current }
      delete updated[userId]
      this._storage.setItem(this._key, updated)
    }
  }
}
